import { useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase/config.js";
import { DEFAULT_LANG, fixedTag, menuTexts, textKey } from "./i18n.js";

// Menú en inglés y portugués: los textos del negocio se traducen una sola vez con IA
// (workflow "MiQRapp - Traducir menú" de n8n) y quedan en traducciones/{usuarioEmpresa}:
//   { en: { <textKey>: "texto traducido" }, pt: { ... }, actualizado }
// El menú lee ese doc; si falta algún texto (producto nuevo o editado) le pide a n8n que
// traduzca lo que falta. El visitante ve el menú en español hasta que llega.
export const TRANSLATE_URL = "https://n8n.miqrapp.com/webhook/miqrapp/traducir";

const askTranslation = async (usuarioEmpresa, lang) => {
    const response = await fetch(TRANSLATE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usuarioEmpresa, lang })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || "translate " + response.status);
    return data.textos && typeof data.textos === "object" ? data.textos : {};
}

// Devuelve { tr, tag, status }: tr(texto) da el texto traducido (o el original si todavía
// no está), tag(etiqueta) lo mismo para etiquetas, y status es "ready", "translating" o "failed".
export const useMenuTranslation = (business, products, lang) => {

    const usuarioEmpresa = business?.usuarioEmpresa;
    const active = lang !== DEFAULT_LANG && !!usuarioEmpresa;
    const [saved, setSaved] = useState(null);
    const [fresh, setFresh] = useState({});
    const [pending, setPending] = useState({});
    const [failed, setFailed] = useState({});
    const asked = useRef({});

    // Una vez que se elige otro idioma queda escuchando el doc: cuando n8n guarda, se actualiza solo.
    const listen = active || saved !== null;
    useEffect(() => {
        if (!listen || !usuarioEmpresa) return;
        return onSnapshot(doc(db, "traducciones", usuarioEmpresa),
            (snap) => setSaved(snap.exists() ? snap.data() : {}),
            () => setSaved({}));
    }, [listen, usuarioEmpresa]);

    const texts = useMemo(() => business && products ? menuTexts(business, products) : [], [business, products]);
    const map = useMemo(() => ({ ...(saved?.[lang] || {}), ...(fresh[lang] || {}) }), [saved, fresh, lang]);
    const missing = active && saved !== null ? texts.filter((t) => !map[textKey(t)]).length : 0;

    useEffect(() => {
        if (!missing || asked.current[lang]) return;
        asked.current[lang] = true;
        setPending((p) => ({ ...p, [lang]: true }));
        askTranslation(usuarioEmpresa, lang)
            .then((textos) => setFresh((f) => ({ ...f, [lang]: { ...(f[lang] || {}), ...textos } })))
            .catch((error) => {
                console.error(error);
                setFailed((f) => ({ ...f, [lang]: true }));
            })
            .finally(() => setPending((p) => ({ ...p, [lang]: false })));
    }, [missing, lang, usuarioEmpresa]);

    const tr = (text) => {
        if (!active || !text) return text;
        return map[textKey(text)] || text;
    };
    const tag = (text) => (active && fixedTag(lang, text)) || tr(text);

    let status = "ready";
    if (active && pending[lang]) status = "translating";
    else if (active && missing && (failed[lang] || asked.current[lang])) status = "failed";

    return { tr, tag, status };
}
